import ability from './index'
import { ConversionType } from '@/constants'

const can = (action, subject, field = null) => ability.can(action, subject, field)


const inSameGroup = (model, userId) => {
    return [model.created_by_id, model.user_created_by_id, model.user_id].includes(userId)
}

/**
 * @param {Object} store
 */
const store = (user, store) => {
    const userId = user.id

    if (Object.keys(store).length === 0) {
        store.user_id = userId
        store.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(store, userId)

    const rules = {
        view_any: can('read_any', 'store'),
        view_group: can('read_group', 'store') && isInSameGroup,
        view_own: can('read_own', 'store') && store.user_id === userId,

        details_any: can('read_any', 'store'),
        details_group: can('read_group', 'store') && isInSameGroup,
        details_own: can('read_own', 'store') && store.user_id === userId,

        create_any: can('write_any', 'store'),
        create_group: can('write_group', 'store'),
        create_own: can('write_own', 'store'),

        edit_any: can('write_any', 'store'),
        edit_group: can('write_group', 'store') && isInSameGroup,
        edit_own: can('write_own', 'store') && store.user_id === userId,

        join: can('execute_any', 'store.join'),
        stop_working: can('execute_any', 'store.join'),

        change_status_any: can('write_any', 'store'),
        write_group: can('write_group', 'store') && isInSameGroup,
        write_own: can('write_own', 'store') && store.user_id === userId,

        api_any: can('execute_any', 'store'),
        api_group: can('execute_group', 'store') && isInSameGroup,
        api_own: can('execute_own', 'store') && store.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        join: rules.join,
        not_join: !rules.join,
        stop_working: rules.stop_working,
        change_status: rules.change_status_any || rules.change_status_group || rules.change_status_own,
        change_need_to_setup: rules.change_status_any || rules.change_status_group || rules.change_status_own,
        api: rules.api_any || rules.api_group || rules.api_own,

        create_any_or_group: rules.create_any || rules.create_group,
        create_own: rules.create_own,
        edit_any_or_group: rules.edit_any || rules.edit_group,
        view_profile: (rules.view_any || rules.view_group) && !rules.view_own,

        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const user = (user, userModel) => {
    const userId = user.id

    if (Object.keys(userModel).length === 0) {
        userModel.id = userId
        userModel.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(userModel, userId)

    const rules = {
        view_any: can('read_any', 'user'),
        view_group: can('read_group', 'user') && isInSameGroup,
        view_own: can('read_own', 'user') && userModel.id === userId,

        details_any: can('read_any', 'user'),
        details_group: can('read_group', 'user') && isInSameGroup,
        details_own: can('read_own', 'user') && userModel.id === userId,

        create_any: can('write_any', 'user'),
        create_group: can('write_group', 'user'),
        create_own: can('write_own', 'user'),

        edit_any: can('write_any', 'user'),
        edit_group: can('write_group', 'user') && isInSameGroup,
        edit_own: can('write_own', 'user') && userModel.id === userId,

        change_status_any: can('write_any', 'user'),
        change_status_group: can('write_group', 'user') && isInSameGroup,
        change_status_own: can('write_own', 'user') && userModel.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'user'),
        soft_delete_group: can('soft_delete_group', 'user') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'user') && userModel.user_id === userId,

        login_as: can('execute_any', 'user.login_as') && userModel.id !== userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        details_any_or_group: rules.details_any || rules.details_group,
        create_any_or_group: rules.create_any || rules.create_group,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        change_status: rules.change_status_any || rules.change_status_group || rules.change_status_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,
        login_as: rules.login_as,
    }
}

const publisher = (user) => {
    const userId = user.id

    const rules = {
        create_any: can('write_any', 'publisher'),
        create_group: can('write_group', 'publisher'),
        create_own: can('write_own', 'publisher') && user.id === userId,

        details_any: can('read_any', 'user'),
        details_group: can('read_group', 'user') && user.id === userId,
        details_own: can('read_own', 'user') && user.id === userId,


        view_any: can('read_any', 'user'),
        view_group: can('read_group', 'user') && user.id === userId,
        view_own: can('read_own', 'user') && user.id === userId,
    }

    return {
        view: rules.details_any || rules.details_group
    }
}

const widget = () => {
    const rules = {
        periodic_total_clicks: can('read_any', 'widget.periodic_total_clicks'),
        last_conversions_list: can('read_any', 'widget.last_conversions_list'),
        periodic_paid_commission: can('read_any', 'widget.periodic_paid_commission'),
        periodic_total_sales: can('read_any', 'widget.periodic_total_sales'),
        periodic_closed_gross_sale: can('read_any', 'widget.periodic_closed_gross_sale'),
        periodic_closed_net_sale: can('read_any', 'widget.periodic_closed_net_sale'),
        periodic_total_inactive_publishers: can('read_any', 'widget.periodic_total_inactive_publishers'),
        periodic_total_active_publishers: can('read_any', 'widget.periodic_total_active_publishers'),
        best_publishers_list: can('read_any', 'widget.best_publishers_list')
    }

    return {
        periodic_total_clicks: rules.periodic_total_clicks,
        last_conversions_list: rules.last_conversions_list,
        periodic_paid_commission: rules.periodic_paid_commission,
        periodic_total_sales: rules.periodic_total_sales,
        periodic_closed_gross_sale: rules.periodic_closed_gross_sale,
        periodic_closed_net_sale: rules.periodic_closed_net_sale,
        periodic_total_inactive_publishers: rules.periodic_total_inactive_publishers,
        periodic_total_active_publishers: rules.periodic_total_active_publishers,
        best_publishers_list: rules.best_publishers_list
    }
}

const pageBuilder = (user, pageBuilder) => {
    const userId = user.id

    if (Object.keys(pageBuilder).length === 0) {
        pageBuilder.user_id = userId
        pageBuilder.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(pageBuilder, userId)

    const rules = {
        view_any: can('read_any', 'page_builder'),
        view_group: can('read_group', 'page_builder') && isInSameGroup,
        view_own: can('read_own', 'page_builder') && pageBuilder.user_id === userId,

        write_any: can('write_any', 'page_builder'),
        write_group: can('write_group', 'page_builder') && isInSameGroup,
        write_own: can('write_own', 'page_builder') && pageBuilder.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'page_builder'),
        soft_delete_group: can('soft_delete_group', 'page_builder') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'page_builder') && pageBuilder.user_id === userId,

        hard_delete_any: can('hard_delete_any', 'page_builder'),
        hard_delete_group: can('hard_delete_group', 'page_builder'),
        hard_delete_own: can('hard_delete_own', 'page_builder')
    }

    return {
        view_list_any: rules.view_any,
        view_list_own_or_group: rules.view_own || rules.view_group,
        write: rules.write_own || rules.write_group || rules.write_any,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,
    }
}

const product = (user, product) => {
    const userId = user.id

    if (Object.keys(product).length === 0) {
        product.user_id = userId
        product.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(product, userId)

    const rules = {
        view_any: can('read_any', 'product'),
        view_group: can('read_group', 'product') && isInSameGroup,
        view_own: can('read_own', 'product') && product.user_id === userId,

        write_any: can('write_any', 'product'),
        write_group: can('write_group', 'product') && isInSameGroup,
        write_own: can('write_own', 'product') && product.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'product'),
        soft_delete_group: can('soft_delete_group', 'product') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'product') && product.user_id === userId,

        hard_delete_any: can('hard_delete_any', 'product'),
        hard_delete_group: can('hard_delete_group', 'product'),
        hard_delete_own: can('hard_delete_own', 'product')
    }

    return {
        view: rules.view_own || rules.view_group || rules.view_any,

        // write: rules.write_own || rules.write_group || rules.write_any,
        // soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,
    }
}

const promotion = (user, promotion) => {
    const userId = user.id

    if (Object.keys(promotion).length === 0) {
        promotion.user_id = userId
        promotion.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(promotion, userId)

    const rules = {
        view_any: can('read_any', 'promotion'),
        view_group: can('read_group', 'promotion') && isInSameGroup,
        view_own: can('read_own', 'promotion') && promotion.user_id === userId,

        details_any: can('read_any', 'promotion'),
        details_group: can('read_group', 'promotion') && isInSameGroup,
        details_own: can('read_own', 'promotion') && promotion.user_id === userId,

        create_any: can('write_any', 'promotion'),
        create_group: can('write_group', 'promotion'),
        create_own: can('write_own', 'promotion'),

        edit_any: can('write_any', 'promotion'),
        edit_group: can('write_group', 'promotion') && isInSameGroup,
        edit_own: can('write_own', 'promotion') && promotion.user_id === userId,

        change_status_any: can('write_any', 'promotion'),
        change_status_group: can('write_group', 'promotion') && isInSameGroup,
        change_status_own: can('write_own', 'promotion') && promotion.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'promotion'),
        soft_delete_group: can('soft_delete_group', 'promotion') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'promotion') && promotion.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,
        change_status: rules.change_status_any || rules.change_status_group || rules.change_status_own,

        create_own: rules.create_own,
        view_any_or_group: rules.view_any || rules.view_group
    }
}

const wallet = (user, wallet) => {
    const userId = user.id

    if (Object.keys(wallet).length === 0) {
        wallet.user_id = userId
        wallet.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(wallet, userId)

    const rules = {
        view_any: can('read_any', 'wallet'),
        view_group: can('read_group', 'wallet') && isInSameGroup,
        view_own: can('read_own', 'wallet') && wallet.user_id === userId,

        write_any: can('write_any', 'wallet'),
        write_group: can('write_group', 'wallet') && isInSameGroup,
        write_own: can('write_own', 'wallet') && wallet.user_id === userId,

        execute_any: can('execute_any', 'wallet'),
        execute_group: can('execute_group', 'wallet') && isInSameGroup,
        execute_own: can('execute_own', 'wallet') && wallet.user_id === userId
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        view_own: rules.view_own,
        view_any_or_group: rules.view_any || rules.view_group,
        write: rules.write_any || rules.write_group || rules.write_own,
        write_any_or_group: rules.write_any || rules.write_group
    }
}

const financial = (user) => {
    const rules = {
        write_any: can('write_any', 'financial'),
        write_group: can('write_group', 'financial'),
        write_own: can('write_own', 'financial'),
    }

    return {
        write: rules.write_any || rules.write_group || rules.write_own,
        write_any_or_group: rules.write_any || rules.write_group
    }
}

const financialReports = (user) => {
    const rules = {
        write_any: can('write_any', 'financial.reports'),
        write_group: can('write_group', 'financial.reports'),
        write_own: can('write_own', 'financial.reports'),
    }

    return {
        write: rules.write_any || rules.write_group || rules.write_own,
        write_any_or_group: rules.write_any || rules.write_group
    }
}

const ticket = (user, ticket) => {
    const userId = user.id

    if (Object.keys(ticket).length === 0) {
        ticket.user_id = userId
        ticket.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(ticket, userId)

    const rules = {
        view_any: can('read_any', 'ticket'),
        view_group: can('read_group', 'ticket') && isInSameGroup,
        view_own: can('read_own', 'ticket') && ticket.user_id === userId,

        details_any: can('read_any', 'ticket'),
        details_group: can('read_group', 'ticket') && isInSameGroup,
        details_own: can('read_own', 'ticket') && (ticket.user_id === userId || ticket.created_by_id === userId || ticket.responsible_id === userId),

        create_any: can('write_any', 'ticket'),
        create_group: can('write_group', 'ticket'),
        create_own: can('write_own', 'ticket'),

        edit_any: can('write_any', 'ticket'),
        edit_group: can('write_group', 'ticket') && isInSameGroup,
        edit_own: can('write_own', 'ticket') && ticket.user_id === userId,

        execute_any: can('execute_any', 'ticket'),
        execute_group: can('execute_group', 'ticket') && isInSameGroup,
        execute_own: can('execute_own', 'ticket') && ticket.user_id === userId,

        be_responsible: can('execute_any', 'ticket.responsible'),
        select_responsible: can('read_any', 'ticket.responsible'),
        view_responsible: can('read_any', 'ticket.responsible'),
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        execute: rules.execute_any || rules.execute_group || rules.execute_own,

        be_responsible: rules.be_responsible,
        select_responsible: rules.select_responsible,
        view_responsible: rules.view_responsible,

        create_any_or_group: rules.create_any || rules.create_group,
        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const link = (user, link) => {
    const userId = user.id

    if (Object.keys(link).length === 0) {
        link.user_id = userId
        link.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(link, userId)

    const rules = {
        view_any: can('read_any', 'link'),
        view_group: can('read_group', 'link') && isInSameGroup,
        view_own: can('read_own', 'link') && link.user_id === userId,

        details_any: can('read_any', 'link'),
        details_group: can('read_group', 'link') && isInSameGroup,
        details_own: can('read_own', 'link') && link.user_id === userId,

        create_any: can('write_any', 'link'),
        create_group: can('write_group', 'link'),
        create_own: can('write_own', 'link'),

        edit_any: can('write_any', 'link'),
        edit_group: can('write_group', 'link') && isInSameGroup,
        edit_own: can('write_own', 'link') && link.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'link'),
        soft_delete_group: can('soft_delete_group', 'link') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'link') && link.user_id === userId,

        update_disable_links_any: can('write_any', 'link'),
        update_disable_links_group: can('write_group', 'link') && isInSameGroup,
        update_disable_links_own: can('write_own', 'link') && link.user_id === userId,

        execute_any: can('execute_any', 'link'),
        execute_group: can('execute_group', 'link'),
        execute_own: can('execute_own', 'link'),
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: (rules.create_any || rules.create_group) || (rules.create_own && store(user, {}).join),
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,

        create_any_or_group: rules.create_any || rules.create_group,
        update_disable_links: rules.update_disable_links_any || rules.update_disable_links_group || rules.update_disable_links_own,
        view_any_or_group: rules.view_any || rules.view_group,

        execute: rules.execute_any || rules.execute_group || rules.execute_own,
        execute_any_or_group: rules.execute_any || rules.execute_group,
    }
}


const commission = (user, commission) => {
    const userId = user.id

    if (Object.keys(commission).length === 0) {
        commission.user_id = userId
        commission.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(commission, userId)

    const rules = {
        view_any: can('read_any', 'commission'),
        view_group: can('read_group', 'commission') && isInSameGroup,
        view_own: can('read_own', 'commission') && commission.user_id === userId,

        details_any: can('read_any', 'commission'),
        details_group: can('read_group', 'commission') && isInSameGroup,
        details_own: can('read_own', 'commission') && commission.user_id === userId,

        create_any: can('write_any', 'commission'),
        create_group: can('write_group', 'commission'),
        create_own: can('write_own', 'commission'),

        edit_any: can('write_any', 'commission'),
        edit_group: can('write_group', 'commission') && isInSameGroup,
        edit_own: can('write_own', 'commission') && commission.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'commission'),
        soft_delete_group: can('soft_delete_group', 'commission') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'commission') && commission.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,

        create_own: rules.create_own,
        create_any_or_group: rules.create_any || rules.create_group,
        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const coupon = (user, coupon) => {
    const userId = user.id

    if (Object.keys(coupon).length === 0) {
        coupon.user_id = userId
        coupon.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(coupon, userId)

    const rules = {
        view_any: can('read_any', 'coupon'),
        view_group: can('read_group', 'coupon') && isInSameGroup,
        view_own: can('read_own', 'coupon') && coupon.user_id === userId,

        details_any: can('read_any', 'coupon'),
        details_group: can('read_group', 'coupon') && isInSameGroup,
        details_own: can('read_own', 'coupon') && coupon.user_id === userId,

        create_any: can('write_any', 'coupon'),
        create_group: can('write_group', 'coupon'),
        create_own: can('write_own', 'coupon'),

        edit_any: can('write_any', 'coupon'),
        edit_group: can('write_group', 'coupon') && isInSameGroup,
        edit_own: can('write_own', 'coupon') && coupon.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'coupon'),
        soft_delete_group: can('soft_delete_group', 'coupon') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'coupon') && coupon.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,

        create_any_or_group: rules.create_any || rules.create_group,
        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const media = (user, media) => {
    const userId = user.id

    if (Object.keys(media).length === 0) {
        media.user_id = userId
        media.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(media, userId)

    const rules = {
        view_any: can('read_any', 'media'),
        view_group: can('read_group', 'media') && isInSameGroup,
        view_own: can('read_own', 'media') && media.user_id === userId,

        details_any: can('read_any', 'media'),
        details_group: can('read_group', 'media') && isInSameGroup,
        details_own: can('read_own', 'media') && media.user_id === userId,

        create_any: can('write_any', 'media'),
        create_group: can('write_group', 'media'),
        create_own: can('write_own', 'media'),

        edit_any: can('write_any', 'media'),
        edit_group: can('write_group', 'media') && isInSameGroup,
        edit_own: can('write_own', 'media') && media.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'media'),
        soft_delete_group: can('soft_delete_group', 'media') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'media') && media.user_id === userId,

        change_status_any: can('write_any', 'media'),
        change_status_group: can('write_group', 'media') && isInSameGroup,
        change_status_own: can('write_own', 'media') && media.user_id === userId,

        change_level_any: can('write_any', 'media'),
        change_level_group: can('write_group', 'media') && isInSameGroup,
        change_level_own: can('write_own', 'media') && media.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,
        change_status: rules.change_status_any || rules.change_status_group || rules.change_status_own,
        change_level: rules.change_level_any || rules.change_level_group || rules.change_level_own,

        create_any_or_group: rules.create_any || rules.create_group,
        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const conversion = (user, conversion) => {
    const userId = user.id

    if (Object.keys(conversion).length === 0) {
        conversion.user_id = userId
        conversion.created_by_id = userId
        conversion.type = ConversionType.SALE
    }

    const isInSameGroup = inSameGroup(conversion, userId)

    // conversion.user_id -> publisher id
    const rules = {
        view_any: can('read_any', 'conversion'),
        view_group: can('read_group', 'conversion') && isInSameGroup,
        view_own: can('read_own', 'conversion') && conversion.created_by_id === userId,

        details_any: can('read_any', 'conversion'),
        details_group: can('read_group', 'conversion') && isInSameGroup,
        details_own: can('read_own', 'conversion') && conversion.created_by_id === userId,

        edit_any: can('write_any', 'conversion'),
        edit_group: can('write_group', 'conversion') && isInSameGroup,
        edit_own: can('write_own', 'conversion') && conversion.created_by_id === userId,

        create_any: can('write_any', 'conversion'),
        create_group: can('write_group', 'conversion'),
        create_own: can('write_own', 'conversion'),
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        edit: (rules.edit_any || rules.edit_group || rules.edit_own), //TODO: we must find better approach && conversion.type === ConversionType.SALE,

        create: rules.create_any || rules.create_group || rules.create_own,
        create_any_or_group: rules.create_any || rules.create_group,

        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const transaction = (user, transaction) => {
    const userId = user.id

    if (Object.keys(transaction).length === 0) {
        transaction.user_id = userId
        transaction.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(transaction, userId)

    const rules = {
        view_any: can('read_any', 'transaction'),
        view_group: can('read_group', 'transaction') && isInSameGroup,
        view_own: can('read_own', 'transaction') && transaction.user_id === userId,

        details_any: can('read_any', 'transaction'),
        details_group: can('read_group', 'transaction') && isInSameGroup,
        details_own: can('read_own', 'transaction') && transaction.user_id === userId,

        edit_any: can('write_any', 'transaction'),
        edit_group: can('write_group', 'transaction') && isInSameGroup,
        edit_own: can('write_own', 'transaction') && transaction.user_id === userId,

        execute_any: can('execute_any', 'transaction'),
        execute_group: can('execute_group', 'transaction') && isInSameGroup,
        execute_own: can('execute_own', 'transaction') && transaction.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        edit: (rules.edit_any || rules.edit_group || rules.edit_own),

        execute_any_or_group: rules.execute_any,
        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const conversation = (user, conversation) => {
    const userId = user.id

    if (Object.keys(conversation).length === 0) {
        conversation.user_id = userId
        conversation.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(conversation, userId)

    const rules = {
        read_any: can('read_any', 'conversation'),
        read_group: can('read_group', 'conversation') && isInSameGroup,
        read_own: can('read_own', 'conversation') && conversation.user_id === userId,

        write_any: can('write_any', 'conversation'),
        write_group: can('write_group', 'conversation') && isInSameGroup,
        write_own: can('write_own', 'conversation') && conversation.user_id === userId,

        execute_any: can('execute_any', 'conversation'),
        execute_group: can('execute_group', 'conversation') && isInSameGroup,
        execute_own: can('execute_own', 'conversation') && conversation.user_id === userId,
    }

    return {
        view_any: rules.read_any,
        view: rules.read_any || rules.read_group || rules.read_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        write: (rules.write_any || rules.write_group || rules.write_own),
        execute: (rules.execute_any || rules.execute_group || rules.execute_own),
    }
}

const agreement = (user, agreement) => {
    const userId = user.id

    if (Object.keys(agreement).length === 0) {
        agreement.id = userId
        agreement.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(agreement, userId)

    const rules = {
        view_any: can('read_any', 'agreement'),
        view_group: can('read_group', 'agreement') && isInSameGroup,
        view_own: can('read_own', 'agreement') && agreement.user_id === userId,

        details_any: can('read_any', 'agreement'),
        details_group: can('read_group', 'agreement') && isInSameGroup,
        details_own: can('read_own', 'agreement') && agreement.user_id === userId,

        create_any: can('write_any', 'agreement'),
        create_group: can('write_group', 'agreement'),
        create_own: can('write_own', 'agreement'),

        edit_any: can('write_any', 'agreement'),
        edit_group: can('write_group', 'agreement') && isInSameGroup,
        edit_own: can('write_own', 'agreement') && agreement.user_id === userId,

        change_status_any: can('write_any', 'agreement'),
        change_status_group: can('write_group', 'agreement') && isInSameGroup,
        change_status_own: can('write_own', 'agreement') && agreement.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'agreement'),
        soft_delete_group: can('soft_delete_group', 'agreement') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'agreement') && agreement.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        view_any_or_group: rules.view_any || rules.view_group,
        details: rules.details_any || rules.details_group || rules.details_own,
        details_any_or_group: rules.details_any || rules.details_group,
        create_any_or_group: rules.create_any || rules.create_group,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        change_status: rules.change_status_any || rules.change_status_group || rules.change_status_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,
    }
}

const campaign = (user, campaign) => {
    const userId = user.id

    if (Object.keys(campaign).length === 0) {
        campaign.id = userId
        campaign.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(campaign, userId)

    const rules = {
        view_any: can('read_any', 'campaign'),
        view_group: can('read_group', 'campaign') && isInSameGroup,
        view_own: can('read_own', 'campaign') && campaign.id === userId,

        details_any: can('read_any', 'campaign'),
        details_group: can('read_group', 'campaign') && isInSameGroup,
        details_own: can('read_own', 'campaign') && campaign.id === userId,

        create_any: can('write_any', 'campaign'),
        create_group: can('write_group', 'campaign'),
        create_own: can('write_own', 'campaign'),

        edit_any: can('write_any', 'campaign'),
        edit_group: can('write_group', 'campaign') && isInSameGroup,
        edit_own: can('write_own', 'campaign') && campaign.id === userId,

        soft_delete_any: can('soft_delete_any', 'campaign'),
        soft_delete_group: can('soft_delete_group', 'campaign') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'campaign') && campaign.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        view_any_or_group: rules.view_any || rules.view_group,
        details: rules.details_any || rules.details_group || rules.details_own,
        details_any_or_group: rules.details_any || rules.details_group,
        create_any_or_group: rules.create_any || rules.create_group,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        edit_any_or_group: rules.edit_any || rules.edit_group,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,
    }
}

const notification = (user, notification) => {
    const userId = user.id

    if (Object.keys(notification).length === 0) {
        notification.user_id = userId
        notification.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(notification, userId)

    const rules = {
        view_any: can('read_any', 'notification'),
        view_group: can('read_group', 'notification') && isInSameGroup,
        view_own: can('read_own', 'notification') && notification.user_id === userId,

        details_any: can('read_any', 'notification'),
        details_group: can('read_group', 'notification') && isInSameGroup,
        details_own: can('read_own', 'notification') && notification.user_id === userId,

        create_any: can('write_any', 'notification'),
        create_group: can('write_group', 'notification'),
        create_own: can('write_own', 'notification'),

        edit_any: can('write_any', 'notification'),
        edit_group: can('write_group', 'notification') && isInSameGroup,
        edit_own: can('write_own', 'notification') && notification.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'notification'),
        soft_delete_group: can('soft_delete_group', 'notification') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'notification') && notification.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,

        create_any_or_group: rules.create_any || rules.create_group,
        view_any_or_group: rules.view_any || rules.view_group,
    }
}

const tag = (user, tag) => {
    const userId = user.id

    if (Object.keys(tag).length === 0) {
        tag.user_id = userId
        tag.created_by_id = userId
    }

    const isInSameGroup = inSameGroup(tag, userId)

    const rules = {
        view_any: can('read_any', 'tag'),
        view_group: can('read_group', 'tag') && isInSameGroup,
        view_own: can('read_own', 'tag') && tag.user_id === userId,

        details_any: can('read_any', 'tag'),
        details_group: can('read_group', 'tag') && isInSameGroup,
        details_own: can('read_own', 'tag') && tag.user_id === userId,

        create_any: can('write_any', 'tag'),
        create_group: can('write_group', 'tag'),
        create_own: can('write_own', 'tag'),

        edit_any: can('write_any', 'tag'),
        edit_group: can('write_group', 'tag') && isInSameGroup,
        edit_own: can('write_own', 'tag') && tag.user_id === userId,

        soft_delete_any: can('soft_delete_any', 'tag'),
        soft_delete_group: can('soft_delete_group', 'tag') && isInSameGroup,
        soft_delete_own: can('soft_delete_own', 'tag') && tag.user_id === userId,
    }

    return {
        view_list: rules.view_any || rules.view_group || rules.view_own,
        details: rules.details_any || rules.details_group || rules.details_own,
        create: rules.create_any || rules.create_group || rules.create_own,
        edit: rules.edit_any || rules.edit_group || rules.edit_own,
        soft_delete: rules.soft_delete_any || rules.soft_delete_group || rules.soft_delete_own,

        create_any_or_group: rules.create_any || rules.create_group,
        view_any_or_group: rules.view_any || rules.view_group,
    }
}


export default {
    wallet,
    product,
    widget,
    store,
    user,
    publisher,
    promotion,
    pageBuilder,
    ticket,
    link,
    commission,
    coupon,
    media,
    conversion,
    transaction,
    conversation,
    financial,
    financialReports,
    agreement,
    campaign,
    notification,
    tag,
}